import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  Paper,
} from "@mui/material";
import { useMemo, useCallback } from "react";
import CollapsibleFormElement from "../../common/collapsible-form-element";
import {
  LanguageCode,
  languageCodeToLabel,
} from "../../../pages/playground/types";
import { useAuthStore } from "../../../authProvider";

// Create an array of available languages for selection
const availableLanguages = Object.values(LanguageCode).map((code) => ({
  id: code,
  name: languageCodeToLabel(code as LanguageCode) || code,
}));

// Stable empty array to avoid unnecessary re-renders
const EMPTY_ARRAY: LanguageCode[] = [];

export default function LanguagesToTranslate({
  languagesToTranslate,
  setLanguagesToTranslate,
  excludedLanguages,
  hasAiTranslationLanguages = false,
}: {
  languagesToTranslate: LanguageCode[];
  setLanguagesToTranslate: (newLanguages: LanguageCode[]) => void;
  excludedLanguages?: LanguageCode[];
  hasAiTranslationLanguages?: boolean;
}) {
  const { user } = useAuthStore();

  const isClientRelated = user?.isClientRelated;

  // Use stable empty array if excludedLanguages is not provided
  const safeExcludedLanguages = excludedLanguages || EMPTY_ARRAY;

  // Check if a language is disabled (excluded and not already selected for legacy data)
  const isLanguageDisabled = useCallback(
    (languageId: LanguageCode) =>
      safeExcludedLanguages.includes(languageId) &&
      !languagesToTranslate.includes(languageId),
    [safeExcludedLanguages, languagesToTranslate]
  );

  // Get only the selectable (non-disabled) languages for select all logic
  const selectableLanguages = useMemo(
    () =>
      availableLanguages.filter(
        (language) => !isLanguageDisabled(language.id as LanguageCode)
      ),
    [isLanguageDisabled]
  );

  // Only show warning when there are AI translation languages, not just because original language is excluded
  const showAiExclusionWarning = hasAiTranslationLanguages;

  // Derive checked state directly from props for stability
  const languageStates = useMemo(() => {
    const states: { [key: string]: boolean } = {};
    availableLanguages.forEach((language) => {
      states[language.id] = languagesToTranslate.includes(
        language.id as LanguageCode
      );
    });
    return states;
  }, [languagesToTranslate]);

  const handleChange =
    (languageId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        // Add language
        setLanguagesToTranslate([
          ...languagesToTranslate,
          languageId as LanguageCode,
        ]);
      } else {
        // Remove language
        setLanguagesToTranslate(
          languagesToTranslate.filter((id) => id !== languageId)
        );
      }
    };

  const handleSelectAll = () => {
    const allSelected = selectableLanguages.every(
      (language) => languageStates[language.id]
    );

    if (allSelected) {
      // Deselect all selectable languages (keep any that might be excluded)
      setLanguagesToTranslate(
        languagesToTranslate.filter(
          (id) => !selectableLanguages.some((lang) => lang.id === id)
        )
      );
    } else {
      // Select all selectable languages (merge with existing)
      const selectableIds = selectableLanguages.map(
        (lang) => lang.id as LanguageCode
      );
      const existingNonSelectable = languagesToTranslate.filter(
        (id) => !selectableIds.includes(id)
      );
      setLanguagesToTranslate([...existingNonSelectable, ...selectableIds]);
    }
  };

  return (
    <CollapsibleFormElement
      title={
        isClientRelated
          ? "Human Translation Languages"
          : "Languages to Translate"
      }
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, display: "block" }}
      >
        {isClientRelated
          ? "Select the languages that phrases should be translated into for the translation service."
          : "Select the languages that phrases should be translated into. During phrasebook search, for each phrase, you'll get an indication on which translations are already available and which ones are missing."}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 2 }}>
        {showAiExclusionWarning && (
          <Typography
            variant="caption"
            color="warning.main"
            sx={{ mb: 2, display: "block" }}
          >
            Some languages are not available because they are configured for AI
            translation.
          </Typography>
        )}

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  selectableLanguages.length > 0 &&
                  selectableLanguages.every(
                    (language) => languageStates[language.id]
                  )
                }
                indeterminate={
                  selectableLanguages.some(
                    (language) => languageStates[language.id]
                  ) &&
                  !selectableLanguages.every(
                    (language) => languageStates[language.id]
                  )
                }
                onChange={handleSelectAll}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" fontWeight="bold">
                Select all languages
              </Typography>
            }
          />
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          {availableLanguages.map((language) => {
            const disabled = isLanguageDisabled(language.id as LanguageCode);
            return (
              <Box
                key={language.id}
                sx={{
                  flexBasis: {
                    xs: "100%",
                    sm: "calc(50%)",
                    md: "calc(25%)",
                  },
                  flexGrow: 0,
                  flexShrink: 0,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!languageStates[language.id]}
                      onChange={handleChange(language.id)}
                      color="primary"
                      size="small"
                      disabled={disabled}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      noWrap
                      color={disabled ? "text.disabled" : "text.primary"}
                    >
                      {language.name}
                    </Typography>
                  }
                  sx={{
                    marginRight: 0,
                    width: "100%",
                    "& .MuiFormControlLabel-label": {
                      width: "calc(100% - 42px)", // Account for checkbox width
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </Paper>
    </CollapsibleFormElement>
  );
}
